import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";

export interface MaintenanceStatus {
  enabled: boolean;
  message: string;
}

/**
 * Holds the maintenance-mode status resolved before the app boots.
 *
 * The status is fetched from the public backend endpoint
 * `GET /api/common/maintenance/` using the native fetch API so it bypasses the
 * auth HTTP interceptor — the endpoint is public and must work before login.
 * The check fails open: any error (timeout, network, non-200) leaves
 * maintenance disabled so a backend blip never locks users out of the app.
 */
@Injectable({ providedIn: "root" })
export class MaintenanceService {
  enabled = false;
  message = "";

  private readonly timeoutMs = 4000;

  async load(): Promise<void> {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.timeoutMs);
      const response = await fetch(`${environment.apiUrl}api/common/maintenance/`, {
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (!response.ok) {
        return;
      }

      const data = (await response.json()) as MaintenanceStatus;
      this.enabled = !!data?.enabled;
      this.message = data?.message ?? "";
    } catch (error) {
      // Fail open: keep the app available if the check cannot complete.
      console.warn("[maintenance] status check failed, continuing", error);
    }
  }
}
